import * as React from 'react';
import { memo, useState } from 'react';
import {
  FaArrowAltCircleUp,
  FaClock,
  FaUser,
  FaWarehouse,
  FaStoreAlt,
  FaCity,
} from 'react-icons/fa';
import { Button } from 'reactstrap';
import { DateTimeHelpers } from '../../tools/DateTimeHelpers';
import { ContainerLocationStatus } from '@/utils/containerLocation';
import { IdName } from '../../components/EditMerchantOrder/HandleStatusChange';
import { ChevronIcon, Collapse, Group, UnstyledButton } from '@mantine/core';
import i18n, { getDateFormatOptions } from '@/utils/i18n';
import { IconButton } from '../../components/Button/Button';
import StatusHistoryTimeline from '../../components/StatusHistoryTimeline';
import { Routes } from '@/utils/routes';
import { ILocation } from './ContainerPage';
import ContainerLocationStatusBadge from '../../components/ContainerLocation/ContainerLocationStatusBadge';

interface ContainerLocationItemProps {
  location: ILocation;
  index: number;
  nextLocationNumber: number | null;
  newDeadline: Date | null;
  containerStations: IdName[];
  selectedStatus: ContainerLocationStatus | null;
  onExtendDeadline: (location: ILocation) => void;
  onSetDeadline: (locationId: number, date: Date | null) => void;
  onSetNewDeadline: (date: Date | null) => void;
  onToggleIsBillable: (location: ILocation) => void;
  onChangeStatus: (
    location: ILocation,
    status: ContainerLocationStatus | null,
  ) => void;
  onStatusChange: (status: ContainerLocationStatus | null) => void;
}

const ContainerLocationItem = memo<ContainerLocationItemProps>(
  ({
    location,
    index,
    nextLocationNumber,
    newDeadline,
    containerStations,
    selectedStatus,
    onExtendDeadline,
    onSetDeadline,
    onSetNewDeadline,
    onToggleIsBillable,
    onChangeStatus,
    onStatusChange,
  }) => {
    const [isStatusHistoryOpen, setIsStatusHistoryOpen] =
      useState<boolean>(false);

    const toggleStatusHistory = () => {
      setIsStatusHistoryOpen(!isStatusHistoryOpen);
    };
    const getContainerLocationStatusEnumFromIntegerString = (
      value: string,
    ): ContainerLocationStatus => {
      const colorInt = parseInt(value, 10);
      const colorString: any = ContainerLocationStatus[colorInt];
      return ContainerLocationStatus[colorString] as any;
    };

    const renderTransactionType = (location: ILocation) => {
      if (location.transactionType != null) {
        return i18n.t('Containers:transactionType', {
          type: location.transactionType,
        });
      }
      if (location.deliveryTokenId !== null) {
        return i18n.t('Containers:transactionTypeDeliveryToken');
      }
      return i18n.t('Containers:transactionTypePartnerApp');
    };

    return (
      <div
        key={`location-${location.id}`}
        id={`location-${location.id}`}
        className="timeline-movement"
      >
        {location.returnDeadline &&
        (ContainerLocationStatus[location.status].startsWith('Overdue') ||
          location.status === ContainerLocationStatus.CheckedOut) ? (
          <div className="timeline-item first">
            <p className="time">
              <span style={{ marginRight: 10 }}>
                <FaClock />{' '}
                {i18n.t('Containers:returnDeadlineLabel') +
                  new Date(location.returnDeadline).toLocaleDateString(
                    i18n.language,
                    getDateFormatOptions(true, undefined, undefined, true),
                  )}
              </span>
            </p>
            <Button onClick={() => onExtendDeadline(location)}>
              {i18n.t('Containers:extendSevenDays')}
            </Button>
            <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center mt-2">
              <Button
                className="me-2 mt-2"
                onClick={() => onSetDeadline(location.id, newDeadline)}
              >
                {i18n.t('Containers:setNewDeadline')}
              </Button>
              <input
                className="mt-2"
                type="date"
                onBlur={(event) => onSetNewDeadline(event.target.valueAsDate)}
                defaultValue={DateTimeHelpers.toDateInputString(
                  location.returnDeadline,
                )}
              />
            </div>
          </div>
        ) : nextLocationNumber != null ? (
          <div className="transaction-card">
            {nextLocationNumber != null && (
              <span
                className={`location-number ${nextLocationNumber % 2 === 0 ? 'color-a' : 'color-b'}`}
              >
                # {nextLocationNumber}
              </span>
            )}
            <div style={{ flexShrink: 0 }}>
              <FaArrowAltCircleUp size={48} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ margin: '0 0 4px 0', fontWeight: 'bold' }}>
                {renderTransactionType(location)}
              </p>
              {location.ownerTill && (
                <p
                  style={{
                    margin: '0 0 4px 0',
                    fontSize: 14,
                    color: '#6c757d',
                  }}
                >
                  {new Date(location.ownerTill).toLocaleDateString(
                    i18n.language,
                    getDateFormatOptions(true, undefined, undefined, true),
                  )}
                </p>
              )}
              {location.isBillableTransaction != null && (
                <p style={{ margin: 0, fontSize: 14 }}>
                  {`${i18n.t('Containers:isBillable')}: ${
                    location.isBillableTransaction
                  } `}
                  <button
                    type="button"
                    className="btn btn-link p-0"
                    style={{ fontSize: 14 }}
                    onClick={() => onToggleIsBillable(location)}
                  >
                    {location.isBillableTransaction
                      ? i18n.t('Containers:setToNonBillable')
                      : i18n.t('Containers:setToBillable')}
                  </button>
                </p>
              )}
            </div>
          </div>
        ) : null}
        <div className="timeline-item" style={{ position: 'relative' }}>
          <span
            className={`location-number ${location.locationNumber % 2 === 0 ? 'color-a' : 'color-b'}`}
          >
            # {location.locationNumber}
          </span>
          <p className="time">
            <span style={{ marginRight: 10 }}>
              <FaClock />{' '}
              {new Date(location.ownerSince).toLocaleDateString(
                i18n.language,
                getDateFormatOptions(true, undefined, undefined, true),
              )}
            </span>
          </p>
          <div className="mb-2">
            <ContainerLocationStatusBadge status={location.status} />
          </div>
          {/*
          <p>
            {i18n.t('Containers:statusSinceLabel') +
              (location.lastStatusUpdateAt === null
                ? i18n.t('Common:unknown')
                : new Date(location.lastStatusUpdateAt).toLocaleDateString(
                    i18n.language,
                    getDateFormatOptions(true, undefined, undefined, true),
                  ))}
          </p> 
          */}
          <UnstyledButton onClick={toggleStatusHistory}>
            <Group position="apart" spacing={0}>
              {i18n.t('Containers:statusHistory')}
              <ChevronIcon
                stroke={'1.5'}
                style={{
                  transform: isStatusHistoryOpen ? `rotate(${-90}deg)` : 'none',
                }}
              />
            </Group>
          </UnstyledButton>
          <Collapse
            in={isStatusHistoryOpen}
            transitionDuration={200}
            className="mb-3"
          >
            <StatusHistoryTimeline statusHistory={location.statusHistory} />
          </Collapse>
          <p
            className="mt-4"
            style={{
              fontWeight: 'bold',
              fontSize: index === 0 ? 20 : undefined,
              wordWrap: 'break-word',
            }}
          >
            {location.owner ? (
              <IconButton
                label={location.owner.username}
                tag="a"
                href={Routes.users(location.ownerId)}
                icon={<FaUser />}
              />
            ) : (
              i18n.t('Containers:nobody')
            )}
          </p>
          <p>
            {location.owner && location.owner.merchant ? (
              <IconButton
                className="my-1"
                label={`${location.owner.merchant.name} ${i18n.t(
                  'Containers:brandLabel',
                )}`}
                tag="a"
                href={Routes.merchants(location.owner?.merchant?.id)}
                icon={<FaCity />}
              />
            ) : null}{' '}
            {location.owner && location.owner.merchantStore ? (
              <IconButton
                className="my-1"
                label={`${location.owner.merchantStore.name} ${i18n.t(
                  'Containers:storeLabel',
                )}`}
                tag="a"
                href={Routes.merchants(
                  location.owner?.merchant?.id,
                  location.owner?.merchantStore?.id,
                )}
                icon={<FaStoreAlt />}
              />
            ) : null}{' '}
            {location.containerStationId && (
              <IconButton
                className="my-1"
                label={
                  containerStations.find(
                    (cs) => cs.id === location.containerStationId,
                  )?.name || location.containerStationId
                }
                tag="a"
                href={Routes.containerStation(
                  location.containerStationId || '',
                )}
                icon={<FaWarehouse />}
              />
            )}
          </p>
          <p></p>

          {location.ownerTill == null &&
            (ContainerLocationStatus[location.status].startsWith('Overdue') ||
              location.status === ContainerLocationStatus.Lost ||
              location.status === ContainerLocationStatus.OnHold ||
              location.status === ContainerLocationStatus.CheckedOut) && (
              <div className="input-group">
                <span className="input-group-text" id="basic-addon1">
                  {i18n.t('Containers:selectStatus')}
                </span>
                <select
                  // @ts-ignore
                  placeholder={i18n.t('Containers:selectStatusPlaceholder')}
                  className="form-control"
                  onChange={(e) =>
                    onStatusChange(
                      getContainerLocationStatusEnumFromIntegerString(
                        e.target.value,
                      ),
                    )
                  }
                  value={selectedStatus || 'default'}
                >
                  {[
                    ContainerLocationStatus.OnHold,
                    ContainerLocationStatus.Lost,
                    ContainerLocationStatus.Overdue,
                    ContainerLocationStatus.OverdueSold,
                    ContainerLocationStatus.CheckedOut,
                  ]
                    .map((status) => (
                      <option
                        value={status}
                        label={ContainerLocationStatus[status]}
                      ></option>
                    ))
                    .concat([
                      <option value={'default'} disabled hidden>
                        {i18n.t('Containers:selectStatusToUpdate')}
                      </option>,
                    ])}
                </select>
                <button
                  className="btn btn-secondary"
                  onClick={() => onChangeStatus(location, selectedStatus)}
                >
                  {i18n.t('Containers:changeStatus')}
                </button>
              </div>
            )}
          {location.ownerTill == null &&
            (location.status === ContainerLocationStatus.InStock ||
              location.status === ContainerLocationStatus.Broken ||
              location.status === ContainerLocationStatus.BrokenAtUser ||
              location.status === ContainerLocationStatus.LostByStore) && (
              <div className="input-group">
                <span className="input-group-text" id="basic-addon1">
                  {i18n.t('Containers:selectStatus')}
                </span>
                <select
                  // @ts-ignore
                  placeholder={i18n.t('Containers:selectStatusPlaceholder')}
                  className="form-control"
                  onChange={(e) =>
                    onStatusChange(
                      getContainerLocationStatusEnumFromIntegerString(
                        e.target.value,
                      ),
                    )
                  }
                  value={selectedStatus || 'default'}
                >
                  {[
                    ContainerLocationStatus.Broken,
                    ContainerLocationStatus.BrokenAtUser,
                    ContainerLocationStatus.LostByStore,
                    ContainerLocationStatus.InStock,
                  ]
                    .map((status) => (
                      <option
                        value={status}
                        label={ContainerLocationStatus[status]}
                      ></option>
                    ))
                    .concat([
                      <option value={'default'} disabled hidden>
                        {i18n.t('Containers:selectStatusToUpdate')}
                      </option>,
                    ])}
                </select>
                <button
                  className="btn btn-secondary"
                  onClick={() => onChangeStatus(location, selectedStatus)}
                >
                  {i18n.t('Containers:changeStatus')}
                </button>
              </div>
            )}
        </div>
      </div>
    );
  },
);

ContainerLocationItem.displayName = 'ContainerLocationItem';

export default ContainerLocationItem;
